/**
 * 操作按钮组件（固定在弹窗底部）
 */
import React, { useState } from 'react';
import { Button, Space, message, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserAddOutlined,
  ToolOutlined,
  GoldOutlined,
  ExportOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { ReportService } from '../../services/reportService';

interface ActionButtonsProps {
  project: ProjectOverviewData;
  onAddWorkTime?: () => void;
  onAddEquipment?: () => void;
  onAddMaterial?: () => void;
  onExport?: (project: ProjectOverviewData) => void;
  onEditProject?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  project,
  onAddWorkTime,
  onAddEquipment,
  onAddMaterial,
  onExport,
  onEditProject,
  isMobile = false
}) => {
  const [exporting, setExporting] = useState(false);

  // 修改项目信息（占位功能）
  const handleEditProject = () => {
    if (onEditProject) {
      onEditProject(project);
    } else {
      message.info('修改项目信息功能开发中...');
    }
  };

  // 导出Excel报表
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await ReportService.exportAndDownloadExcel(
        project.id,
        project.name
      );
      message.success('Excel报表导出成功');
    } catch (error) {
      console.error('导出Excel失败:', error);
      message.error('导出Excel报表失败');
    } finally {
      setExporting(false);
    }
  };

  // 导出PDF报表
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await ReportService.exportAndDownloadPDF(
        project.id,
        project.name
      );
      message.success('PDF报表导出成功');
    } catch (error) {
      console.error('导出PDF失败:', error);
      message.error('导出PDF报表失败');
    } finally {
      setExporting(false);
    }
  };

  // 导出菜单
  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'excel',
      label: '导出Excel',
      icon: <FileExcelOutlined />,
      onClick: handleExportExcel
    },
    {
      key: 'pdf',
      label: '导出PDF',
      icon: <FilePdfOutlined />,
      onClick: handleExportPDF
    }
  ];

  // 移动端布局
  if (isMobile) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 8,
        padding: '12px 0'
      }}>
        {/* 第一行：三个添加按钮 */}
        <div style={{ 
          display: 'flex', 
          gap: 8,
          justifyContent: 'space-between'
        }}>
          <Button
            icon={<UserAddOutlined />}
            onClick={onAddWorkTime}
            size="small"
            style={{ flex: 1 }}
          >
            添加工时
          </Button>
          <Button
            icon={<ToolOutlined />}
            onClick={onAddEquipment}
            size="small"
            style={{ flex: 1 }}
          >
            添加设备
          </Button>
          <Button
            icon={<GoldOutlined />}
            onClick={onAddMaterial}
            size="small"
            style={{ flex: 1 }}
          >
            添加材料
          </Button>
        </div>
        
        {/* 第二行：修改项目信息 */}
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEditProject}
          size="middle"
          block
        >
          修改项目信息
        </Button>
        
        {/* 第三行：导出报表 */}
        <Dropdown menu={{ items: exportMenuItems }} placement="topCenter">
          <Button
            icon={<ExportOutlined />}
            loading={exporting}
            size="middle"
            block
          >
            导出报表 <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    );
  }

  // PC端布局
  return (
    <div style={{ padding: '16px 0' }}>
      {/* 第一行：三个添加按钮等宽排列 */}
      <div style={{ 
        display: 'flex', 
        gap: 12,
        marginBottom: 12
      }}>
        <Button
          icon={<UserAddOutlined />}
          onClick={onAddWorkTime}
          style={{ flex: 1 }}
        >
          添加工时
        </Button>
        <Button
          icon={<ToolOutlined />}
          onClick={onAddEquipment}
          style={{ flex: 1 }}
        >
          添加设备
        </Button>
        <Button
          icon={<GoldOutlined />}
          onClick={onAddMaterial}
          style={{ flex: 1 }}
        >
          添加材料
        </Button>
      </div>
      
      {/* 第二行：修改项目信息（左） + 导出报表（右） */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEditProject}
        >
          修改项目信息
        </Button>
        
        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
          <Button
            icon={<ExportOutlined />}
            loading={exporting}
          >
            导出报表 <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

export default ActionButtons;
